import React from 'react';
import * as XLSX from 'xlsx';
import { Link } from 'react-router-dom';
import { QuizApiClient } from '@/infrastructure/quiz/QuizApiClient';
import { getAbsoluteAppUrl } from '@/utils/appBase';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { quizDifficultyLabel } from '@/i18n/quizDifficulty';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

const emptyQuestion = (order = 1) => ({
  id: `local-${Date.now()}-${order}-${Math.random().toString(36).slice(2, 8)}`,
  question_text: '',
  question_type: 'choice',
  choices: ['', '', '', ''],
  answer: '',
  hint: '',
  explanation: '',
  points: 10,
  image_url: '',
});

const rowKeyMap = (row) => {
  const lowerMap = {};
  Object.keys(row || {}).forEach((k) => {
    lowerMap[k.toLowerCase()] = row[k];
  });
  return lowerMap;
};

const collectChoicesFromExcelRow = (row) => {
  const m = rowKeyMap(row);
  const optKeys = Object.keys(m)
    .filter((k) => /^option_(\d+)$/.test(k))
    .map((k) => ({ n: Number(k.match(/^option_(\d+)$/)[1]), k }))
    .sort((a, b) => a.n - b.n);
  let vals = optKeys.map(({ k }) => String(m[k] || '').trim()).filter(Boolean);
  if (vals.length === 0) {
    const chKeys = Object.keys(m)
      .filter((k) => /^choice_(\d+)$/.test(k))
      .map((k) => ({ n: Number(k.match(/^choice_(\d+)$/)[1]), k }))
      .sort((a, b) => a.n - b.n);
    vals = chKeys.map(({ k }) => String(m[k] || '').trim()).filter(Boolean);
  }
  if (vals.length === 0) {
    ['option_a', 'option_b', 'option_c', 'option_d'].forEach((k) => {
      const v = String(m[k] || '').trim();
      if (v) vals.push(v);
    });
  }
  return vals;
};

const extractImageUrlFromExcelRow = (row) => {
  const m = rowKeyMap(row);
  const tryKeys = ['image_url', 'url', 'image', 'link', 'media_url'];
  for (const k of tryKeys) {
    const v = String(m[k] || '').trim();
    if (v && /^https?:\/\//i.test(v)) return v;
  }
  for (const k of Object.keys(m).sort()) {
    if (/^url_\d+$/.test(k)) {
      const v = String(m[k] || '').trim();
      if (v && /^https?:\/\//i.test(v)) return v;
    }
  }
  return '';
};

const mapRowsToQuestions = (rows) => {
  const get = (row, keys) => {
    for (const k of keys) {
      if (row[k] != null && String(row[k]).trim()) return String(row[k]).trim();
      const found = Object.keys(row).find((key) => key.toLowerCase() === k.toLowerCase());
      if (found && String(row[found]).trim()) return String(row[found]).trim();
    }
    return '';
  };

  return rows
    .map((row, idx) => {
      const questionText = get(row, ['question', 'question_text', 'cau_hoi']);
      const answer = get(row, ['answer', 'dap_an']);
      const typeRaw = get(row, ['type', 'question_type', 'loai']);
      const questionType = typeRaw.toLowerCase() === 'input' ? 'input' : 'choice';
      const fromNumbered = collectChoicesFromExcelRow(row);
      const choices =
        fromNumbered.length > 0
          ? fromNumbered
          : [
              get(row, ['option_a', 'a']),
              get(row, ['option_b', 'b']),
              get(row, ['option_c', 'c']),
              get(row, ['option_d', 'd']),
            ].filter(Boolean);
      if (!questionText || !answer) return null;
      if (questionType === 'choice' && choices.length < 2) return null;
      const idRaw = get(row, ['id', 'question_id', 'stt']);
      const imageUrl = extractImageUrlFromExcelRow(row);
      return {
        id: idRaw || `imp-${idx}-${Math.random().toString(36).slice(2, 8)}`,
        question_text: questionText,
        question_type: questionType,
        choices: questionType === 'choice' ? choices : [],
        answer,
        hint: get(row, ['hint', 'goi_y']),
        explanation: get(row, ['explanation', 'giai_thich']),
        points: Number(get(row, ['points', 'diem']) || 10),
        image_url: imageUrl,
      };
    })
    .filter(Boolean);
};

const SHUFFLE_Q_TAG = 'shuffle_questions:';
const SHUFFLE_C_TAG = 'shuffle_choices:';

const parseShuffleBoolTag = (tags, prefix, fallback = false) => {
  const found = (Array.isArray(tags) ? tags : []).find((t) => typeof t === 'string' && t.startsWith(prefix));
  if (!found) return fallback;
  const raw = String(found.slice(prefix.length)).trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
};

const mergeShuffleSettingsToTags = (rawTags, shuffleQuestions, shuffleChoices) => {
  const tags = Array.isArray(rawTags) ? rawTags : [];
  const cleaned = tags.filter(
    (t) => typeof t === 'string' && !t.startsWith(SHUFFLE_Q_TAG) && !t.startsWith(SHUFFLE_C_TAG)
  );
  cleaned.push(`${SHUFFLE_Q_TAG}${shuffleQuestions ? '1' : '0'}`);
  cleaned.push(`${SHUFFLE_C_TAG}${shuffleChoices ? '1' : '0'}`);
  return cleaned;
};

function QuestionCard({ index, question, onChange, onRemove, t }) {
  const updateChoice = (choiceIdx, value) => {
    const next = [...question.choices];
    next[choiceIdx] = value;
    onChange({ ...question, choices: next });
  };

  return (
    <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h4 className="font-headline text-xl font-bold">{t('quizEditor.questionCardTitle', { n: index + 1 })}</h4>
        <button type="button" onClick={onRemove} className="rounded-lg px-3 py-1 text-sm text-rose-600 hover:bg-rose-50">
          {t('quizEditor.delete')}
        </button>
      </div>

      <textarea
        value={question.question_text}
        onChange={(e) => onChange({ ...question, question_text: e.target.value })}
        placeholder={t('quizEditor.qTextPh')}
        className="mb-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
        rows={3}
      />

      <div className="mb-3 grid gap-3 md:grid-cols-3">
        <select
          value={question.question_type}
          onChange={(e) => {
            const nextType = e.target.value === 'input' ? 'input' : 'choice';
            onChange({
              ...question,
              question_type: nextType,
              choices: nextType === 'choice' ? (question.choices?.length ? question.choices : ['', '', '', '']) : [],
            });
          }}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="choice">{t('quizEditor.typeChoice')}</option>
          <option value="input">{t('quizEditor.typeInput')}</option>
        </select>
        <input
          type="number"
          value={question.points}
          onChange={(e) => onChange({ ...question, points: Number(e.target.value || 10) })}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder={t('quizEditor.pointsPh')}
        />
        <input
          type="text"
          value={question.answer}
          onChange={(e) => onChange({ ...question, answer: e.target.value })}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder={t('quizEditor.answerCorrectPh')}
        />
      </div>

      {question.question_type === 'choice' ? (
        <div className="mb-3 space-y-2">
          {(question.choices?.length ? question.choices : ['', '', '', '']).map((_, idx) => (
            <input
              key={idx}
              type="text"
              value={question.choices?.[idx] || ''}
              onChange={(e) => updateChoice(idx, e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm md:max-w-xl"
              placeholder={t('quizEditor.choicePh', { letter: String.fromCharCode(65 + idx) })}
            />
          ))}
          <button
            type="button"
            onClick={() =>
              onChange({
                ...question,
                choices: [...(question.choices || []), ''],
              })
            }
            className="text-sm font-semibold text-cyan-700 hover:underline"
          >
            {t('quizEditor.addChoice')}
          </button>
        </div>
      ) : null}

      <div className="mb-3">
        <label className="text-xs font-semibold text-slate-500">{t('quizEditor.imageUrlLabel')}</label>
        <input
          type="text"
          value={question.image_url || ''}
          onChange={(e) => onChange({ ...question, image_url: e.target.value })}
          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder={t('quizEditor.imageUrlPh')}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <input
          type="text"
          value={question.hint}
          onChange={(e) => onChange({ ...question, hint: e.target.value })}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder={t('quizEditor.hintPh')}
        />
        <input
          type="text"
          value={question.explanation}
          onChange={(e) => onChange({ ...question, explanation: e.target.value })}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder={t('quizEditor.explanationPh')}
        />
      </div>
    </div>
  );
}

export default function QuizEditorPage({ session, quizId = null }) {
  const { t } = useAppTranslation();
  const api = React.useMemo(() => new QuizApiClient(), []);
  const token = session?.accessToken || null;
  const [loading, setLoading] = React.useState(Boolean(quizId));
  const [saving, setSaving] = React.useState(false);
  const [result, setResult] = React.useState('');
  const [sheetUrl, setSheetUrl] = React.useState('');
  const [form, setForm] = React.useState({
    title: '',
    description: '',
    level_id: 'general',
    subject_id: 'ielts',
    difficulty: 'medium',
    estimated_time: 30,
    time_limit_minutes: 30,
    pass_score: 70,
    timer_mode: 'total',
    question_time_seconds: 30,
    shuffle_questions: false,
    shuffle_choices: false,
    is_public: true,
    allow_anonymous: true,
    tags: [],
    questions: [emptyQuestion(1)],
    share_code: '',
  });

  if (!token) {
    return (
      <div className="mx-auto max-w-4xl rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="font-headline text-2xl font-extrabold">{t('quizEditor.needLoginTitle')}</h1>
        <p className="mt-2 text-sm text-rose-700">{t('quizEditor.needLoginBody')}</p>
      </div>
    );
  }

  React.useEffect(() => {
    let mounted = true;
    if (!quizId || !token) return undefined;
    api
      .getQuizById(quizId, token)
      .then((data) => {
        if (!mounted) return;
        const tags = Array.isArray(data?.tags) ? data.tags : [];
        setForm({
          title: data?.title || '',
          description: data?.description || '',
          level_id: data?.level_id || 'general',
          subject_id: data?.subject_id || 'general',
          difficulty: data?.difficulty || 'medium',
          estimated_time: Number(data?.estimated_time || 30),
          time_limit_minutes: Number(data?.time_limit_minutes || 30),
          pass_score: Number(data?.pass_score || 70),
          timer_mode: data?.timer_mode || 'total',
          question_time_seconds: Number(data?.question_time_seconds || 30),
          shuffle_questions: parseShuffleBoolTag(tags, SHUFFLE_Q_TAG, false),
          shuffle_choices: parseShuffleBoolTag(tags, SHUFFLE_C_TAG, false),
          is_public: Number(data?.is_public || 0) === 1,
          allow_anonymous: Number(data?.allow_anonymous || 1) === 1,
          tags,
          questions: Array.isArray(data?.questions) && data.questions.length > 0
            ? data.questions
            : [emptyQuestion(1)],
          share_code: data?.share_code || '',
        });
      })
      .catch((e) => setResult(formatApiErrorMessage(e?.message, t) || t('errors.loadQuizEditor')))
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, [api, quizId, token, t]);

  const setQuestions = (nextQuestions) => {
    setForm((prev) => ({
      ...prev,
      questions: nextQuestions.map((q, idx) => ({ ...q, question_order: idx + 1 })),
    }));
  };

  const onImportExcel = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const questions = mapRowsToQuestions(rows);
      if (questions.length === 0) {
        setResult(t('errors.parseExcel'));
        return;
      }
      setQuestions(questions);
      setResult(t('quizEditor.importedExcel', { count: questions.length }));
    } catch (e) {
      setResult(formatApiErrorMessage(e?.message, t) || t('errors.importExcel'));
    }
  };

  const onImportGoogleSheet = async () => {
    if (!sheetUrl || !token) return;
    setSaving(true);
    setResult('');
    try {
      const payload = {
        title: form.title || `Quiz import ${new Date().toLocaleString()}`,
        description: form.description,
        level_id: form.level_id,
        subject_id: form.subject_id,
        difficulty: form.difficulty,
        estimated_time: form.estimated_time,
        time_limit_minutes: form.time_limit_minutes,
        timer_mode: form.timer_mode,
        question_time_seconds: form.question_time_seconds,
        tags: mergeShuffleSettingsToTags(form.tags, form.shuffle_questions, form.shuffle_choices),
        pass_score: form.pass_score,
        is_public: form.is_public,
        allow_anonymous: form.allow_anonymous,
        source_url: sheetUrl,
        creator_name: session?.user?.name || 'Creator',
      };
      const data = await api.importQuizFromGoogleSheet(payload, token);
      setResult(t('quizEditor.importedSheet', { id: data.id, code: data.share_code }));
      setForm((prev) => ({ ...prev, share_code: data.share_code || prev.share_code }));
    } catch (e) {
      setResult(formatApiErrorMessage(e?.message, t) || t('errors.importSheet'));
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (!token) return;
    setSaving(true);
    setResult('');
    try {
      const payload = {
        ...form,
        tags: mergeShuffleSettingsToTags(form.tags, form.shuffle_questions, form.shuffle_choices),
        creator_name: session?.user?.name || 'Creator',
      };
      let data;
      if (quizId) {
        data = await api.updateQuiz(quizId, payload, token);
      } else {
        data = await api.createQuiz(payload, token);
      }
      const code = data?.share_code || form.share_code;
      setForm((prev) => ({ ...prev, share_code: code || '' }));
      const linkSuffix = code ? ` | Link public: ${getAbsoluteAppUrl(`/play/${code}`)}` : '';
      setResult(t('quizEditor.saveSuccess', { code: code || 'N/A', linkSuffix }));
    } catch (e) {
      setResult(formatApiErrorMessage(e?.message, t) || t('errors.saveQuiz'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="mx-auto max-w-5xl rounded-2xl bg-white p-8">{t('quizEditor.loading')}</div>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="rounded-[2rem] bg-white p-8 shadow-sm lg:col-span-8">
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-700">{t('quizEditor.essenceTag')}</p>
          <input
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            placeholder={t('quizEditor.titlePlaceholder')}
            className="mt-4 w-full border-none p-0 text-3xl font-headline font-extrabold focus:ring-0"
          />
          <textarea
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder={t('quizEditor.descPlaceholder')}
            className="mt-4 w-full rounded-xl border border-slate-100 px-3 py-2 text-sm"
            rows={4}
          />
        </div>

        <div className="space-y-4 lg:col-span-4">
          <div className="rounded-[2rem] bg-white p-6 shadow-sm">
            <p className="mb-3 text-sm font-bold text-slate-700">{t('quizEditor.paramTitle')}</p>
            <div className="space-y-3">
              <input
                value={form.subject_id}
                onChange={(e) => setForm((prev) => ({ ...prev, subject_id: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('quizEditor.subjectPlaceholder')}
              />
              <select
                value={form.difficulty}
                onChange={(e) => setForm((prev) => ({ ...prev, difficulty: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="easy">{quizDifficultyLabel(t, 'easy')}</option>
                <option value="medium">{quizDifficultyLabel(t, 'medium')}</option>
                <option value="hard">{quizDifficultyLabel(t, 'hard')}</option>
              </select>
              <input
                type="number"
                value={form.time_limit_minutes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, time_limit_minutes: Number(e.target.value || 30) }))
                }
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('quizEditor.timeLimitPh')}
              />
              <select
                value={form.timer_mode}
                onChange={(e) => setForm((prev) => ({ ...prev, timer_mode: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="total">{t('quizEditor.timerModeTotal')}</option>
                <option value="per_question">{t('quizEditor.timerModePerQ')}</option>
              </select>
              {form.timer_mode === 'per_question' ? (
                <input
                  type="number"
                  min={5}
                  max={600}
                  value={form.question_time_seconds}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, question_time_seconds: Number(e.target.value || 30) }))
                  }
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder={t('quizEditor.questionTimePh')}
                />
              ) : null}
              <input
                type="number"
                value={form.pass_score}
                onChange={(e) => setForm((prev) => ({ ...prev, pass_score: Number(e.target.value || 70) }))}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('quizEditor.passScorePh')}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_public: e.target.checked }))}
                />
                {t('quizEditor.publicLabel')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.allow_anonymous}
                  onChange={(e) => setForm((prev) => ({ ...prev, allow_anonymous: e.target.checked }))}
                />
                {t('quizEditor.anonymousLabel')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.shuffle_questions}
                  onChange={(e) => setForm((prev) => ({ ...prev, shuffle_questions: e.target.checked }))}
                />
                {t('quizEditor.shuffleQ')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.shuffle_choices}
                  onChange={(e) => setForm((prev) => ({ ...prev, shuffle_choices: e.target.checked }))}
                />
                {t('quizEditor.shuffleChoices')}
              </label>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-headline text-xl font-bold">{t('quizEditor.importSectionTitle')}</h3>
          <Link
            to="/help/quiz-google-sheet"
            className="text-sm font-semibold text-cyan-700 underline decoration-cyan-200 underline-offset-2 hover:text-cyan-800"
          >
            {t('quizEditor.importHelpLink')}
          </Link>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-semibold">{t('quizEditor.sheetUrlLabel')}</label>
            <input
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              placeholder={t('quizEditor.sheetUrlPlaceholder')}
            />
            <button
              onClick={onImportGoogleSheet}
              disabled={saving}
              className="rounded-xl bg-cyan-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              {t('quizEditor.importSheetBtn')}
            </button>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold">{t('quizEditor.excelLabel')}</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onImportExcel(file);
              }}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
            <p className="text-xs text-slate-500">{t('quizEditor.excelColumnsHint')}</p>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-headline text-2xl font-extrabold">{t('quizEditor.questionsSectionTitle')}</h3>
          <button
            onClick={() => setQuestions([...form.questions, emptyQuestion(form.questions.length + 1)])}
            className="rounded-xl border border-cyan-200 px-4 py-2 text-sm font-bold text-cyan-700"
          >
            {t('quizEditor.addQuestion')}
          </button>
        </div>
        {form.questions.map((question, idx) => (
          <QuestionCard
            key={question.id || idx}
            index={idx}
            question={question}
            onChange={(next) => {
              const all = [...form.questions];
              all[idx] = next;
              setQuestions(all);
            }}
            onRemove={() => {
              const all = form.questions.filter((_, i) => i !== idx);
              setQuestions(all.length > 0 ? all : [emptyQuestion(1)]);
            }}
            t={t}
          />
        ))}
      </section>

      <section className="rounded-[2rem] bg-slate-900 p-6 text-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-slate-300">{t('quizEditor.shareCodeLabel')}</p>
            <p className="text-xl font-extrabold">{form.share_code || t('quizEditor.shareCodeNotCreated')}</p>
          </div>
          <button
            onClick={onSave}
            disabled={saving}
            className="rounded-xl bg-white px-5 py-3 text-sm font-bold text-slate-900 disabled:opacity-60"
          >
            {saving ? t('quizEditor.saving') : quizId ? t('quizEditor.saveEdit') : t('quizEditor.createQuiz')}
          </button>
        </div>
        {result ? <p className="mt-3 rounded-xl bg-white/10 px-3 py-2 text-sm">{result}</p> : null}
      </section>
    </div>
  );
}

